import { Button, Card, Modal } from 'antd';
import arrayMove from 'array-move';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import ConfirmPopup from '../ConfirmPopup';
import EditableGroupName from './EditableGroupName';
import GroupPanelTable from './GroupPanelTable';
import { validateItemLabel, validateItemName } from './groupPanelUtils';
import VisibilityButton from './VisibilityButton';

const ADD_MODE = 'ADD_MODE';
const EDIT_MODE = 'EDIT_MODE';

class GroupPanel extends React.Component {
  state = {
    isDeleteGroupModalVisible: false,
    isToggleGroupVisibilityModalVisible: false
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { items, onBatchUpdateItems, groupIndex } = this.props;
    if (oldIndex !== newIndex) {
      const newData = arrayMove([].concat(items), oldIndex, newIndex).filter(
        (el) => !!el
      );
      onBatchUpdateItems(newData, groupIndex, true);
    }
  };

  onToggleDeleteGroupModal = () => {
    const { isDeleteGroupModalVisible } = this.state;
    this.setState({
      isDeleteGroupModalVisible: !isDeleteGroupModalVisible
    });
  };

  onToggleGroupVisibilityModal = () => {
    const { isToggleGroupVisibilityModalVisible } = this.state;
    this.setState({
      isToggleGroupVisibilityModalVisible: !isToggleGroupVisibilityModalVisible
    });
  };

  onConfirmToggleGroupVisibility = () => {
    const { groupIndex, onToggleGroupVisibility } = this.props;
    if (onToggleGroupVisibility) {
      onToggleGroupVisibility(groupIndex);
      this.setState({
        isToggleGroupVisibilityModalVisible: false
      });
    }
  };

  onConfirmDelete = () => {
    const { groupIndex, onDeleteGroup } = this.props;
    const { isDeleteGroupModalVisible } = this.state;
    if (onDeleteGroup) {
      onDeleteGroup(groupIndex);
      this.setState({
        isDeleteGroupModalVisible: !isDeleteGroupModalVisible
      });
    }
  };

  onToggleGroupVisiblitySwitch = (isChecked) => {
    if (!isChecked) {
      this.onToggleGroupVisibilityModal();
    } else {
      this.onConfirmToggleGroupVisibility();
    }
  };

  onConfirmDeleteItem = (item) => {
    const { items, onBatchUpdateItems, groupIndex } = this.props;
    if (item) {
      const itemIndex = item.index;
      let updatedItems = [];

      if (Array.isArray(items)) {
        items.forEach((dataItem) => {
          if (itemIndex !== dataItem.index) {
            updatedItems.push(dataItem);
          }
        });

        // fix indexes
        const updatedItemsIndexes = updatedItems.map(
          (dataItem, currentDataIndex) => {
            return {
              ...dataItem,
              index: currentDataIndex
            };
          }
        );

        onBatchUpdateItems(updatedItemsIndexes, groupIndex, true);
      }
    }
  };

  onAddNewItem = () => {
    const { groupItem, items, onUpdateGroup, groupIndex } = this.props;
    const updatedData = [...items];

    if (!groupItem.isCurrentlyAddingAnItem) {
      updatedData.push({
        key: `${items.length + 1}`,
        itemName: null,
        labelName: null,
        isVisible: true,
        isEditing: false,
        isAdding: true,
        index: items.length,
        filterEnabled: false,
        sortEnabled: false,
        searchEnabled: false
      });

      const updatedGroup = {
        ...groupItem,
        items: updatedData,
        isCurrentlyAddingAnItem: true
      };
      onUpdateGroup(updatedGroup, groupIndex);
    }
  };

  onGroupNameChange = (groupName) => {
    const { groupItem, onUpdateGroup, groupIndex } = this.props;

    const updatedGroup = {
      ...groupItem,
      groupName
    };
    onUpdateGroup(updatedGroup, groupIndex, true);
  };

  onToggleItemVisiblity = (item, editMode, isSaving) => {
    const { items, onBatchUpdateItems, groupIndex } = this.props;
    const updatedItems = [];

    items.forEach((currentItem) => {
      if (currentItem.index === item.index) {
        if (editMode === ADD_MODE) {
          updatedItems.push({
            ...currentItem,
            isVisible: !currentItem.isVisible
          });
        } else if (editMode === EDIT_MODE) {
          const updatedEditData = {
            ...currentItem.editData,
            isVisible: !currentItem.editData.isVisible
          };
          updatedItems.push({
            ...currentItem,
            editData: updatedEditData
          });
        }
      } else {
        updatedItems.push(currentItem);
      }
    });

    // don't save when it's in edit or add mode
    onBatchUpdateItems(updatedItems, groupIndex, isSaving);
  };

  onColumnNameChange = (value, item, editMode) => {
    const {
      items,
      onBatchUpdateItems,
      groupIndex,
      labelDictionary
    } = this.props;
    const updatedItems = [];
    items.forEach((currentItem) => {
      if (currentItem.index === item.index) {
        let errorMessage = validateItemName(value, items, item);

        if (editMode === ADD_MODE) {
          updatedItems.push({
            ...currentItem,
            itemName: value,
            labelName: labelDictionary[value],
            itemNameErrorMessage: errorMessage
          });
        } else if (editMode === EDIT_MODE) {
          const updatedEditData = {
            ...currentItem.editData,
            itemName: value,
            itemNameErrorMessage: errorMessage
          };
          updatedItems.push({
            ...currentItem,
            editData: updatedEditData
          });
        }
      } else {
        updatedItems.push(currentItem);
      }
    });

    onBatchUpdateItems(updatedItems, groupIndex, false);
  };

  onItemLabelChange = (e, item, editMode) => {
    const value = e.target.value;
    const { lastShownLabelWarning } = this.state;
    const {
      groupItem,
      items,
      onBatchUpdateItems,
      onUpdateGroup,
      groupIndex
    } = this.props;
    const updatedItems = items.map((currentItem) => {
      if (currentItem.index === item.index) {
        //show warning for changing the labels
        if (
          this.props.labelDictionary[item.itemName] &&
          this.props.labelDictionary[item.itemName] !== value &&
          lastShownLabelWarning != item.itemName
        ) {
          const { warning } = Modal;
          warning({
            title: `Label for \'${item.itemName}\' will be overwritten`
          });
        }
        //set state for last whown label
        this.setState({ lastShownLabelWarning: item.itemName });

        let errorMessage = null;
        errorMessage = validateItemLabel(value, items, item);

        if (editMode === ADD_MODE) {
          return {
            ...currentItem,
            labelName: value,
            labelNameErrorMessage: errorMessage
          };
        } else if (editMode === EDIT_MODE) {
          const updatedEditData = {
            ...currentItem.editData,
            labelName: value,
            labelNameErrorMessage: errorMessage
          };
          return {
            ...currentItem,
            editData: updatedEditData
          };
        }
      } else {
        return currentItem;
      }
    });

    const updatedGroup = {
      ...groupItem,
      items: updatedItems,
      focusedLabelNameIndex: item.index
    };
    onUpdateGroup(updatedGroup, groupIndex, false);
  };

  onConfirmAddItem = (item) => {
    const {
      groupItem,
      items,
      onBatchUpdateItems,
      onUpdateGroup,
      groupIndex
    } = this.props;
    const updatedItems = [];

    let isValidItem = false;
    let labelNameErrorMessage = null;
    let itemNameErrorMessage = null;

    // check label and item name
    labelNameErrorMessage = validateItemLabel(item.labelName, items, item);
    itemNameErrorMessage = validateItemName(item.itemName, items, item);
    isValidItem = every([
      isEmpty(itemNameErrorMessage),
      isEmpty(labelNameErrorMessage)
    ]);

    if (isValidItem) {
      items.forEach((currentItem) => {
        if (currentItem.index === item.index) {
          updatedItems.push({
            ...item,
            isAdding: false,
            labelNameErrorMessage: labelNameErrorMessage,
            itemNameErrorMessage: itemNameErrorMessage
          });
        } else {
          updatedItems.push(currentItem);
        }
      });

      const updatedGroup = {
        ...groupItem,
        items: updatedItems,
        focusedLabelNameIndex: null,
        isCurrentlyAddingAnItem: false
      };
      onUpdateGroup(updatedGroup, groupIndex, true);
    } else {
      items.forEach((currentItem) => {
        if (currentItem.index === item.index) {
          updatedItems.push({
            ...item,
            labelNameErrorMessage: labelNameErrorMessage,
            itemNameErrorMessage: itemNameErrorMessage
          });
        } else {
          updatedItems.push(currentItem);
        }
      });

      onBatchUpdateItems(updatedItems, groupIndex, false);
    }
  };

  onConfirmEditItem = (item) => {
    const {
      groupItem,
      items,
      onBatchUpdateItems,
      onUpdateGroup,
      groupIndex
    } = this.props;
    const updatedItems = [];

    let isValidItem = false;
    let labelNameErrorMessage = null;
    let itemNameErrorMessage = null;

    const { editData } = item;

    // check label and item name
    labelNameErrorMessage = validateItemLabel(editData.labelName, items, item);
    itemNameErrorMessage = validateItemName(editData.itemName, items, item);
    isValidItem = every([
      isEmpty(itemNameErrorMessage),
      isEmpty(labelNameErrorMessage)
    ]);

    if (isValidItem) {
      items.forEach((currentItem) => {
        if (currentItem.index === item.index) {
          const { editData } = item;
          updatedItems.push({
            ...editData,
            isEditing: false
          });
        } else {
          updatedItems.push(currentItem);
        }
      });

      const updatedGroup = {
        ...groupItem,
        items: updatedItems,
        focusedLabelNameIndex: null
      };
      onUpdateGroup(updatedGroup, groupIndex, true);
    } else {
      items.forEach((currentItem) => {
        if (currentItem.index === item.index) {
          const { editData } = item;
          updatedItems.push({
            editData: {
              ...editData,
              labelNameErrorMessage,
              itemNameErrorMessage
            },
            isEditing: true
          });
        } else {
          updatedItems.push(currentItem);
        }
      });

      onBatchUpdateItems(updatedItems, groupIndex, false);
    }
  };

  onEditItem = (item) => {
    const { items, onBatchUpdateItems, groupIndex } = this.props;
    const updatedItems = items.map((currentItem) => {
      if (currentItem.index === item.index) {
        return {
          ...currentItem,
          isEditing: true,
          editData: {
            ...currentItem
          }
        };
      }
      return currentItem;
    });

    onBatchUpdateItems(updatedItems, groupIndex, false);
  };

  onToggleItemOperation = (item, field, mode, isSaving) => {
    const { items, onBatchUpdateItems, groupIndex } = this.props;
    const updatedItems = items.map((currentItem) => {
      if (currentItem.index === item.index) {
        if (mode === ADD_MODE) {
          currentItem[field] = !currentItem[field];

          return currentItem;
        } else if (mode === EDIT_MODE) {
          var updatedEditData = {
            ...currentItem.editData
          };
          updatedEditData[field] = !updatedEditData[field];

          return {
            ...currentItem,
            editData: updatedEditData
          };
        }
      }
      return currentItem;
    });
    onBatchUpdateItems(updatedItems, groupIndex, isSaving);
  };

  renderAddItemButton = () => {
    const { groupItem } = this.props;
    if (!groupItem.isDefault) {
      return (
        <Button
          onClick={this.onAddNewItem}
          type="link"
          style={{ padding: '20px' }}>
          <p>
            <i className="fa fa-plus"></i> Add item
          </p>
        </Button>
      );
    }

    return null;
  };

  renderGroupActions = () => {
    const { groupItem } = this.props;

    return (
      <div>
        <span style={{ display: 'inline-block', fontSize: '16px' }}>
          <VisibilityButton
            groupItem={groupItem}
            onToggleGroupVisiblity={this.onToggleGroupVisiblitySwitch}
          />
        </span>
        {!groupItem.isDefault && (
          <span
            style={{
              color: '#DF6057',
              cursor: 'pointer',
              display: 'inline-block',
              marginLeft: '10px'
            }}
            onClick={this.onToggleDeleteGroupModal}>
            x
          </span>
        )}
      </div>
    );
  };

  render() {
    const { groupItem, items, columnNames, groups } = this.props;
    const { focusedLabelNameIndex } = groupItem;
    const {
      isDeleteGroupModalVisible,
      isToggleGroupVisibilityModalVisible
    } = this.state;

    if (groupItem) {
      return (
        <div>
          <ConfirmPopup
            isModalOpen={isDeleteGroupModalVisible}
            handleOk={this.onConfirmDelete}
            togglePopUp={this.onToggleDeleteGroupModal}
            headerText="Are you sure you want to delete the column?"
            bodyText="The column will be deleted here and will be removed from the widget"
            confirmText="Yes"
            cancelText="No"
            isLoading={false}
          />

          <ConfirmPopup
            isModalOpen={isToggleGroupVisibilityModalVisible}
            handleOk={this.onConfirmToggleGroupVisibility}
            togglePopUp={this.onToggleGroupVisibilityModal}
            headerText="Are you sure you want to hide this column?"
            bodyText="The column will be hidden from the widget until you enable it to be visible again."
            confirmText="Yes"
            cancelText="No"
            isLoading={false}
          />

          <Card
            headStyle={{ background: '#EEF4FA' }}
            bodyStyle={{ padding: 0 }}
            title={
              <EditableGroupName
                onConfirmGroupNameChange={this.onGroupNameChange}
                groups={groups}
                groupName={groupItem.groupName}
              />
            }
            extra={this.renderGroupActions()}>
            <GroupPanelTable
              items={items}
              isGroupDefault={groupItem.isDefault ? true : false}
              focusedLabelNameIndex={focusedLabelNameIndex}
              columnNames={columnNames}
              labelDictionary={this.props.labelDictionary}
              onItemLabelChange={this.onItemLabelChange}
              onConfirmAddItem={this.onConfirmAddItem}
              onConfirmEditItem={this.onConfirmEditItem}
              onEditItem={this.onEditItem}
              onToggleItemVisiblity={this.onToggleItemVisiblity}
              onConfirmDeleteItem={this.onConfirmDeleteItem}
              onColumnNameChange={this.onColumnNameChange}
              onSortEnd={this.onSortEnd}
              onToggleItemOperation={this.onToggleItemOperation}
            />

            {this.renderAddItemButton()}
          </Card>
        </div>
      );
    }

    return null;
  }
}

/**
 * Proptypes
 * @type {
 * groups,
 * columnNames,
 * groupIndex,
 * onBatchUpdateItems,
 * onUpdateGroup,
 * groupItem,
 * items,
 * onDeleteGroup,
 * onToggleGroupVisibility,
 * labelDictionary
 * }
 */
GroupPanel.propTypes = {
  groups: PropTypes.array.isRequired,
  columnNames: PropTypes.array.isRequired,
  groupIndex: PropTypes.number.isRequired,
  onBatchUpdateItems: PropTypes.func.isRequired,
  onUpdateGroup: PropTypes.func.isRequired,
  groupItem: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  onDeleteGroup: PropTypes.func.isRequired,
  onToggleGroupVisibility: PropTypes.func.isRequired,
  labelDictionary: PropTypes.object
};

export default GroupPanel;
